/**
 * Engagements.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as auditsApi from '../api/Audits';
import * as analysisApi from '../api/Analysis';
import { parsed } from '../api/parse';
import type { ModuleCode } from '../features/modules/modules';
import { z } from 'zod';
import {
	auditDetailSchema,
	auditSummarySchema,
	selectedFileSchema,
	type AuditDetail,
	type CanonicalRowFilter,
} from '../schemas';
import { keys } from './keys';

/** One engagement, in full. */
export function useAuditQuery(id: string | null) {
	return useQuery({
		queryKey: keys.audits.detail(id ?? ''),
		queryFn: async () =>
			parsed(
				auditDetailSchema,
				await auditsApi.getAudit(id as string),
				`/audits/${id}`,
			),
		enabled: Boolean(id),
	});
}

export interface CreateAuditInput {
	clientId: string;
	name: string;
	periodStart: string;
	periodEnd: string;
	module: ModuleCode;
	/** Frozen onto the audit always, empty or not. */
	filter: CanonicalRowFilter;
	analysisSlugs: string[];
	/** Start processing straight away. */
	run: boolean;
}

/** Replace an audit's chosen analyses ("Edit analyses"). The server drops the
 *  removed ones with their exceptions and adds the new ones as not run, so
 *  everything about this audit is read again. */
export function useUpdateAuditAnalysesMutation(auditId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (analysisSlugs: string[]) =>
			parsed(
				auditDetailSchema,
				await auditsApi.setAuditAnalysisSlugs(auditId, analysisSlugs),
				`/audits/${auditId}`,
			),
		onSuccess: () => {
			/* The whole `audits` prefix: this audit's detail, analyses, exceptions
         and availability, and the list's counts. */
			void client.invalidateQueries({ queryKey: keys.audits.all });
		},
	});
}

/** Create an engagement, freeze its snapshot, and optionally start it. */
export function useCreateAuditMutation() {
	const client = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateAuditInput): Promise<AuditDetail> => {
			const created = parsed(
				auditDetailSchema,
				await auditsApi.createAudit({
					clientId: input.clientId,
					name: input.name,
					periodStart: input.periodStart,
					periodEnd: input.periodEnd,
					module: input.module,
				}),
				'/audits',
			);

			/* Always, even when empty: freezing the snapshot is what gives an audit
         its rows, and an empty filter means every row this client has. */
			await auditsApi.setAuditDataFilter(created.pkAuditId, input.filter);

			if (input.analysisSlugs.length > 0) {
				await auditsApi.setAuditAnalysisSlugs(
					created.pkAuditId,
					input.analysisSlugs,
				);
			}

			if (input.run) {
				/* Runs every chosen analysis on the server. A failure to start must
           not lose an audit that is otherwise correct; the Analyses tab shows
           what ran and offers Run analyses again. */
				try {
					await analysisApi.runAnalyses(created.pkAuditId);
				} catch {
					// shown on the Analyses tab
				}
			}

			return created;
		},
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: keys.audits.all });
			/* The clients list carries each client's audit count. */
			void client.invalidateQueries({ queryKey: keys.clients.all });
		},
	});
}

/** This module's engagements, optionally one client's. */
export function useAuditsQuery(module: ModuleCode, clientId?: string) {
	return useQuery({
		queryKey: [...keys.audits.list(module), clientId ?? ''] as const,
		queryFn: async () =>
			parsed(
				z.array(auditSummarySchema),
				await auditsApi.getAudits(module, clientId),
				`/audits?module=${module}`,
			),
	});
}

/** The audit's own file list. Richer than the `files` on `AuditDetail`: these
 * rows carry the per-file classification coverage the data tab draws. */
export function useAuditFilesQuery(auditId: string | null) {
	return useQuery({
		queryKey: keys.audits.files(auditId ?? ''),
		queryFn: async () =>
			parsed(
				z.array(selectedFileSchema),
				await auditsApi.getAuditFiles(auditId as string),
				`/audits/${auditId}/files`,
			),
		enabled: Boolean(auditId),
	});
}

/** Re-freeze the audit's snapshot. The endpoint answers `{ rowCount }` only,
 * so the screen's fuller picture comes from the invalidation below. */
export function useChangeDataFilterMutation(auditId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (filter: CanonicalRowFilter) =>
			parsed(
				z.object({ rowCount: z.number() }),
				await auditsApi.setAuditDataFilter(auditId, filter),
				`/audits/${auditId}/data-filter`,
			),
		onSuccess: () => {
			void client.invalidateQueries({
				queryKey: keys.audits.detail(auditId),
			});
			void client.invalidateQueries({
				queryKey: keys.audits.files(auditId),
			});
			void client.invalidateQueries({
				queryKey: keys.audits.analyses(auditId),
			});
		},
	});
}
